import { memo, useMemo } from "react";
import { IonLabel } from "@ionic/react";
import { shallow } from "zustand/shallow";
import { DAILY_CASH_PRIZE_AUD_DISPLAY_LABEL } from "../../../../../../Common/constants/prizes";
import WLPhoneVerification from "../../../../../components/WLPhoneVerification/WLPhoneVerification";
import { useWebUserStore } from "../../../../../CommonStores";
import LoginOTPInterfaceStore from "../../../interface/LoginOTPInterface";

import s from "./LoginOTPVerificationPanel.module.scss";

/** Stable default so `useMemo` deps don’t change every render when the store has no handler */
const defaultOnVerifySuccess = (): void => {
  // no-op after OTP when parent did not wire a callback
};

const LoginOTPVerificationPanel: React.FC = (): JSX.Element | null => {
  const phoneNumber = useWebUserStore((state) => state.userPhoneNumber);

  const [
    userId,
    verificationType,
    greetingFirstName,
    fixPaymentGiveawayHighlight,
    handleVerifySuccess,
  ] = LoginOTPInterfaceStore(
    (state) => [
      state.userId,
      state.verificationType,
      state.greetingFirstName,
      state.fixPaymentGiveawayHighlight,
      state.handleVerifySuccess,
    ],
    shallow
  );

  const onVerifySuccess = handleVerifySuccess ?? defaultOnVerifySuccess;

  const phoneVerification = useMemo(() => {
    if (!phoneNumber || !userId) {
      return null;
    }

    switch (verificationType) {
      case "cancellationVerification":
        return (
          <WLPhoneVerification
            userId={userId}
            phoneNumber={phoneNumber}
            title="VERIFY ACCOUNT"
            descriptionComponent={() => (
              <IonLabel className="win-body regular gilroy white">
                Hey {greetingFirstName} - we just need to verify your mobile
                number to continue. This helps protect your account from
                unauthorised cancellations.
              </IonLabel>
            )}
            bottomDetailsComponent={() => (
              <IonLabel className="win-body regular gilroy white">
                If you cancel, you’ll lose:
                <ul className={s.cancelLoseList}>
                  <li className="win-body regular gilroy white">
                    Your entries in upcoming draws
                  </li>
                  <li className="win-body regular gilroy white">
                    Your chance at {DAILY_CASH_PRIZE_AUD_DISPLAY_LABEL} cash daily!
                  </li>
                  <li className="win-body regular gilroy white">
                    Daily Locker rewards
                  </li>
                  <li className="win-body regular gilroy white">
                    Member-only offers
                  </li>
                </ul>
              </IonLabel>
            )}
            onVerifySuccess={onVerifySuccess}
          />
        );
      case "fixPaymentVerification":
        return (
          <WLPhoneVerification
            userId={userId}
            phoneNumber={phoneNumber}
            title="VERIFY ACCOUNT"
            descriptionComponent={() => (
              <>
                <IonLabel className="win-body regular gilroy white">
                  Hey {greetingFirstName} - you’ll need to fix your billing
                  details to get back in the draw for the{" "}
                  <IonLabel className="win-body semibold gilroy secondary-500">
                    {fixPaymentGiveawayHighlight}
                  </IonLabel>
                </IonLabel>
                <IonLabel className="win-body regular gilroy white">
                  Verify your mobile number and we’ll help you get it sorted.
                </IonLabel>
              </>
            )}
            onVerifySuccess={onVerifySuccess}
          />
        );
      default:
        return (
          <WLPhoneVerification
            userId={userId}
            phoneNumber={phoneNumber}
            title="VERIFY ACCOUNT"
            description="Enter the code we sent to the mobile number below to verify your
          mobile number."
            onVerifySuccess={onVerifySuccess}
          />
        );
    }
  }, [
    fixPaymentGiveawayHighlight,
    greetingFirstName,
    onVerifySuccess,
    phoneNumber,
    userId,
    verificationType,
  ]);

  return phoneVerification;
};

export default memo(LoginOTPVerificationPanel);
